import { useMemo, useRef, type KeyboardEvent, type PointerEvent } from "react";

/** How a popup's trigger was last worked; null when neither was seen (assistive tech, a script). */
export type OpenedBy = "pointer" | "keyboard" | null;

/** The keys that open a popup from its trigger (the arrows open a dropdown menu). */
const OPENING_KEYS = new Set(["Enter", " ", "ArrowDown", "ArrowUp"]);

/**
 * How a popup's trigger was last worked, by the keyboard or by a pointer: spread `triggerProps` on
 * the trigger (or on an element its events bubble through) and read `openedBy` as the popup opens
 * or closes. Only an opening key counts: a Tab passing over the trigger does not. A keyboard open
 * moves focus into the popup and shows its ring; after a pointer, focus must not land where a ring
 * would show (WebKit rings any programmatic focus after a tap, whatever :focus-visible says
 * elsewhere). Null is treated as the keyboard, which keeps the default focus.
 *
 * `openedFrom` is the button that was worked (the one the event came from, when the props sit on a
 * wrapper): focusOpenedFrom hands focus back to it on close.
 */
export function useOpenedBy() {
  const openedBy = useRef<OpenedBy>(null);
  const openedFrom = useRef<HTMLElement | null>(null);
  const triggerProps = useMemo(() => {
    const worked = (by: OpenedBy, target: EventTarget) => {
      openedBy.current = by;
      openedFrom.current = target instanceof Element ? target.closest("button") : null;
    };
    return {
      onKeyDown: (event: KeyboardEvent) => {
        if (OPENING_KEYS.has(event.key)) worked("keyboard", event.target);
      },
      onPointerDown: (event: PointerEvent) => worked("pointer", event.target),
    };
  }, []);
  return { openedBy, openedFrom, triggerProps };
}

export type OpenTriggerProps = ReturnType<typeof useOpenedBy>["triggerProps"];

/** useOpenedBy's two refs, read-only: how a popup was opened, and from which button. */
export type OpenedByRefs = {
  openedBy: { readonly current: OpenedBy };
  openedFrom: { readonly current: HTMLElement | null };
};

/**
 * A popup's onCloseAutoFocus: focus goes back to the button it was opened from, ringed for the
 * keyboard only. Radix's own close focus lands on its trigger with a ring WebKit shows after a tap,
 * or, with no Radix trigger (a sheet opened from a plain button), on nothing. focusVisible is a
 * no-op where unsupported. Without a button seen (assistive tech, a script), Radix's default stays.
 */
export function focusOpenedFrom(event: Event, { openedBy, openedFrom }: OpenedByRefs) {
  const button = openedFrom.current;
  if (!button?.isConnected) return;
  event.preventDefault();
  const options: FocusOptions & { focusVisible?: boolean } = {
    preventScroll: true,
    focusVisible: openedBy.current !== "pointer",
  };
  button.focus(options);
}
